/**
 * Number Formatting and Styling Utilities
 * Functions for displaying and styling numeric values
 */

/**
 * Parse a trimmed string or number as a positive base-10 integer (≥ 1).
 * @param {string|number} raw
 * @param {number} fallback Returned when empty or invalid (e.g. 0 to signal validation failure)
 * @returns {number}
 */
export function parsePositiveInt(raw, fallback = 1) {
  const n = Number.parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(n) || n < 1) {
    return fallback;
  }
  return n;
}

/**
 * Colors negative numbers red in the DOM.
 * Gets all numbers in the document that have the class "total" and colors them red if
 * they are negative.
 * 
 * @param {number} attempt - Current attempt number for retries (default: 1)
 */
export const colorNegativeNumbers = (attempt = 1) => {
  const totalElements = document.getElementsByClassName("total");
  const spanElements = Array.from(totalElements).map((element) =>
    element.querySelector("span")
  );
  let allDashes = true;
  
  spanElements.forEach((spanElement) => {
    if (spanElement) {
      const contents = spanElement.childNodes[0]?.nodeValue;
      if (contents && contents.includes("-") && contents !== "-") {
        spanElement.style.color = "red";
        allDashes = false;
      } else {
        spanElement.style.color = "black";
        if (contents !== "-") {
          allDashes = false;
        }
      }
    }
  });
  
  // Retry if all values are dashes (data might still be loading)
  if (allDashes && attempt < 10) {
    setTimeout(() => colorNegativeNumbers(attempt + 1), 500);
  }
};

/**
 * Format a number as currency
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (default: 'USD')
 * @param {string} locale - Locale for formatting (default: 'en-US')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'en-US') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

/**
 * Format a number with specific decimal places
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @param {boolean} useGrouping - Whether to use thousands separators (default: true)
 * @param {boolean} trimTrailingZeros - When true, omit trailing fractional zeros (default: false)
 * @returns {string} Formatted number string
 */
export const formatNumber = (
  num,
  decimals = 2,
  useGrouping = true,
  trimTrailingZeros = false
) => {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: trimTrailingZeros ? 0 : decimals,
    maximumFractionDigits: decimals,
    useGrouping: useGrouping,
  });
};

/**
 * Format a percentage value
 * @param {number} value - Value to format as percentage (0-1 or 0-100)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @param {boolean} isDecimal - Whether input is decimal (0-1) or percentage (0-100)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1, isDecimal = true) => {
  const percentage = isDecimal ? value * 100 : value;
  return `${formatNumber(percentage, decimals)}%`;
};

/**
 * Apply conditional styling to numeric values
 * @param {number} value - Numeric value to style
 * @param {Object} options - Styling options
 * @param {string} options.positiveColor - Color for positive values (default: 'green')
 * @param {string} options.negativeColor - Color for negative values (default: 'red')
 * @param {string} options.zeroColor - Color for zero values (default: 'gray')
 * @returns {Object} Style object with color property
 */
export const getNumericValueStyle = (value, options = {}) => {
  const {
    positiveColor = 'green',
    negativeColor = 'red',
    zeroColor = 'gray'
  } = options;

  if (value > 0) return { color: positiveColor };
  if (value < 0) return { color: negativeColor };
  return { color: zeroColor };
};
